import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { sellerService } from '../services/apiService';
import SellerListingForm from '../components/Seller/SellerListingForm';
import ListingDetailsModal from '../components/Seller/ListingDetailsModal';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 1rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.5rem 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
  }
`;

const Header = styled.div`
  max-width: 1400px;
  margin: 0 auto 2rem;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  color: #000000;
  position: relative;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-sizing: border-box;

  h1 {
    margin: 0 0 0.5rem 0;
    color: #000000;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.5px;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    margin: 0;
    color: #4b5563;
    font-size: 0.95rem;
    font-weight: 500;
    position: relative;
    z-index: 1;
  }
`;

const TabContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TabButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 100%;
  }
`;

const TabButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: ${props => props.active ? '#1e293b' : 'white'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  border: 1px solid ${props => props.active ? '#1e293b' : '#e5e7eb'};
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.25px;

  &:hover {
    border-color: #1e293b;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  ${props => props.active && `
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `}
`;

const ListingsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ListingsGrid = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 1.5rem;
`;

const ListingsTable = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1.2fr 0.8fr;
  gap: 1rem;
  padding: 1.2rem;
  background: #1e293b;
  border-bottom: 2px solid #667eea;
  font-weight: 700;
  color: white;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 2fr 1.2fr 0.8fr 0.6fr 0.8fr 0.7fr;
    gap: 0.7rem;
    padding: 0.9rem 1rem;
    font-size: 0.75rem;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1.2fr 0.8fr;
  gap: 1rem;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  align-items: center;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:nth-child(even) {
    background: rgba(102, 126, 234, 0.02);
  }

  &:hover {
    background: rgba(102, 126, 234, 0.08);
  }

  @media (max-width: 992px) {
    grid-template-columns: 2fr 1.2fr 0.8fr 0.6fr 0.8fr 0.7fr;
    gap: 0.7rem;
    padding: 0.9rem 1rem;
    font-size: 0.8rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem 0.75rem;
    border-left: 4px solid #667eea;
    margin-bottom: 0.75rem;
    background: white;
    border-radius: 8px;
    border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  }
`;

const TableCell = styled.div`
  font-size: 0.85rem;
  color: #4b5563;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 992px) {
    font-size: 0.75rem;
  }

  @media (max-width: 600px) {
    font-size: 0.7rem;
    white-space: normal;
    word-break: break-word;
  }

  &.crop-name {
    font-weight: 600;
    color: #000000;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.location {
    color: #666;
  }

  &.price {
    font-weight: 600;
    color: #059669;
  }

  &.status-cell {
    padding: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.7rem;
  border-radius: 14px;
  font-size: 0.65rem;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  background: ${props => {
    if (props.status === 'Approved') return '#059669';
    if (props.status === 'Rejected') return '#dc2626';
    return '#fbbf24';
  }};
  white-space: nowrap;
`;

const ViewButton = styled.button`
  padding: 0.35rem 0.7rem;
  background: #1e293b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.65rem;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.1px;
  white-space: nowrap;

  &:hover {
    background: #0f172a;
    transform: translateY(-1px);
  }

  @media (max-width: 600px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.6rem;
  }
`;

const RefreshButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: #1e293b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.25px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    background: #0f172a;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ListingCard = styled.div`
  background: white;
  border-radius: 6px;
  padding: 0.6rem 0.7rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-left: 3px solid ${props => {
    if (props.status === 'Approved') return '#059669';
    if (props.status === 'Rejected') return '#dc2626';
    return '#fbbf24';
  }};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  height: 180px;
  max-height: 180px;
  overflow: hidden;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  h3 {
    margin: 0 0 0.25rem 0;
    color: #000000;
    font-size: 0.8rem;
    font-weight: 700;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    margin: 0.12rem 0;
    color: #4b5563;
    font-size: 0.65rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;

    strong {
      color: #1e293b;
      font-weight: 600;
      display: inline;
      font-size: 0.65rem;
      margin: 0;
    }

    a {
      color: #667eea;
      text-decoration: none;
      font-weight: 600;
      word-break: break-all;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .status-badge {
    display: inline-block;
    margin-top: 0.2rem;
    padding: 0.2rem 0.5rem;
    border-radius: 10px;
    font-size: 0.55rem;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.1px;
    background: ${props => {
      if (props.status === 'Approved') return '#059669';
      if (props.status === 'Rejected') return '#dc2626';
      return '#fbbf24';
    }};
    align-self: flex-start;
    white-space: nowrap;
  }

  .status-info {
    font-size: 0.6rem;
    margin-top: 0.1rem;
    font-weight: 500;
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;

    &.approved {
      color: #059669;
    }

    &.pending {
      color: #f59e0b;
    }

    &.rejected {
      color: #dc2626;
    }
  }

  .card-header {
    flex-shrink: 0;
    margin-bottom: 0.15rem;
  }

  .card-content {
    flex: 1;
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
  }

  .card-status {
    flex-shrink: 0;
    margin-top: 0.15rem;
    padding-top: 0.1rem;
  }
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px dashed #e0e0e0;

  h3 {
    color: #666;
    margin-bottom: 0.75rem;
    font-size: 1.2rem;
  }

  p {
    color: #999;
    font-size: 0.95rem;
  }
`;

const LoadingContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  p {
    color: #666;
    font-size: 0.95rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.4rem;
  flex-wrap: wrap;

  button {
    flex: 1;
    min-width: 70px;
    padding: 0.35rem 0.6rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.6rem;
    transition: all 0.2s ease;
    background: #1e293b;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.15px;
    white-space: nowrap;

    &:hover {
      background: #0f172a;
      transform: translateY(-0.5px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }

    @media (max-width: 480px) {
      padding: 0.3rem 0.5rem;
      font-size: 0.55rem;
    }
  }
`;

const CampaignCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    border-color: #d1d5db;
  }

  h3 {
    margin: 0 0 0.75rem 0;
    color: #1e293b;
    font-size: 1.1rem;
    font-weight: 700;
  }

  p {
    margin: 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  strong {
    color: #059669;
    font-weight: 600;
  }
`;

const CampaignDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  .detail {
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 6px;
    border-left: 3px solid #667eea;

    .label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #667eea;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      margin-bottom: 0.25rem;
    }

    .value {
      font-size: 0.95rem;
      font-weight: 600;
      color: #1e293b;
    }
  }
`;

const UpvoteButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.upvoted ? '#059669' : '#e5e7eb'};
  color: ${props => props.upvoted ? 'white' : '#4b5563'};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.2px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const CampaignsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;


const SellerDashboard = () => {
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const [campaignTogglingId, setCampaignTogglingId] = useState(null);

  const sellerId = parseInt(localStorage.getItem('userId'));
  const sellerName = localStorage.getItem('userName') || 'Seller';

  useEffect(() => {
    loadListings();
  }, [refreshKey]);

  // Load campaigns when campaigns tab is activated
  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns();
    }
  }, [activeTab]);

  const loadListings = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📊 [SellerDashboard] Loading listings for sellerId:', sellerId);
      const data = await sellerService.getListings(sellerId);
      console.log('✅ [SellerDashboard] Listings loaded:', data);
      setListings(data || []);
    } catch (error) {
      console.error('❌ [SellerDashboard] Error loading listings:', error);
      setError(error.message || 'Failed to load listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      console.log('📢 [SellerDashboard] Loading live campaigns');
      const data = await sellerService.getLiveCampaigns();
      console.log('✅ [SellerDashboard] Campaigns loaded:', data);
      setCampaigns(data || []);
    } catch (error) {
      console.error('❌ [SellerDashboard] Error loading campaigns:', error);
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const handleCampaignUpvote = async (campaignId, isCurrentlyUpvoted) => {
    try {
      setCampaignTogglingId(campaignId);
      await sellerService.toggleCampaignUpvote(campaignId, !isCurrentlyUpvoted);
      
      // Update campaigns state
      setCampaigns(campaigns.map(c => 
        c.campaignId === campaignId ? { ...c, isUpvoted: !isCurrentlyUpvoted } : c
      ));
    } catch (error) {
      console.error('Error updating campaign upvote:', error);
      alert('Failed to update your interest in this campaign');
    } finally {
      setCampaignTogglingId(null);
    }
  };

  const refresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleShowDetails = (listing) => {
    setSelectedListing(listing);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedListing(null);
  };

  return (
    <DashboardContainer>
      <Header>
        <h1>👨‍🌾 Welcome, {sellerName}!</h1>
        <p>Manage your crop listings and track approval status</p>
      </Header>

      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          color: '#7f1d1d',
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '6px',
          fontWeight: 'bold'
        }}>
          ⚠️ Error loading listings: {error}
        </div>
      )}

      <TabContainer>
        <TabButtons>
          <TabButton 
            active={activeTab === 'listings'} 
            onClick={() => setActiveTab('listings')}
          >
            📋 My Listings ({listings.length})
          </TabButton>
          <TabButton 
            active={activeTab === 'campaigns'} 
            onClick={() => setActiveTab('campaigns')}
          >
            📢 Live Campaigns
          </TabButton>
          <TabButton 
            active={activeTab === 'create'} 
            onClick={() => setActiveTab('create')}
          >
            ➕ Add New Listing
          </TabButton>
        </TabButtons>

        {activeTab === 'listings' && (
          <ListingsContainer>
            <RefreshButton onClick={refresh}>
              🔄 Refresh Listings
            </RefreshButton>

            {loading ? (
              <LoadingContainer>
                <p>⏳ Loading your listings...</p>
              </LoadingContainer>
            ) : listings.length === 0 ? (
              <EmptyState>
                <h3>No listings yet</h3>
                <p>Click "Add New Listing" to create your first crop listing</p>
              </EmptyState>
            ) : (
              <ListingsTable>
                <TableHeader>
                  <div>Crop Name</div>
                  <div>Location</div>
                  <div>Price</div>
                  <div>Area</div>
                  <div>Status</div>
                  <div>Action</div>
                </TableHeader>
                {listings.map(listing => (
                  <TableRow key={listing.listingId}>
                    <TableCell className="crop-name">
                      🌾 {listing.cropType}{listing.breedVariety ? ` - ${listing.breedVariety}` : ''}
                    </TableCell>
                    <TableCell className="location">
                      {listing.region}, {listing.state}
                    </TableCell>
                    <TableCell className="price">
                      ₹{listing.basePrice}/kg
                    </TableCell>
                    <TableCell>
                      {listing.cultivationAreaInAcres > 0 ? `${listing.cultivationAreaInAcres} ac` : '—'}
                    </TableCell>
                    <TableCell className="status-cell">
                      <StatusBadge status={listing.status}>
                        {listing.status === 'Approved' && '✓ '}
                        {listing.status === 'Rejected' && '✗ '}
                        {listing.status === 'Pending' && '⏳ '}
                        {listing.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ViewButton onClick={() => handleShowDetails(listing)}>View</ViewButton>
                    </TableCell>
                  </TableRow>
                ))}
              </ListingsTable>
            )}
          </ListingsContainer>
        )}

        {activeTab === 'campaigns' && (
          <ListingsContainer>
            <RefreshButton onClick={loadCampaigns}>
              🔄 Refresh Campaigns
            </RefreshButton>

            {campaignsLoading ? (
              <LoadingContainer>
                <p>⏳ Loading live campaigns...</p>
              </LoadingContainer>
            ) : campaigns.length === 0 ? (
              <EmptyState>
                <h3>No live campaigns available</h3>
                <p>Check back soon for new buying campaigns</p>
              </EmptyState>
            ) : (
              <CampaignsGrid>
                {campaigns.map(campaign => (
                  <CampaignCard key={campaign.campaignId}>
                    <h3>🎯 {campaign.campaignName}</h3>
                    
                    <CampaignDetails>
                      <div className="detail">
                        <div className="label">Crop Type</div>
                        <div className="value">{campaign.cropType}</div>
                      </div>
                      <div className="detail">
                        <div className="label">Target Quantity</div>
                        <div className="value">{campaign.targetQuantity} kg</div>
                      </div>
                      <div className="detail">
                        <div className="label">Price Per Unit</div>
                        <div className="value"><strong>₹{campaign.pricePerUnit}/kg</strong></div>
                      </div>
                      <div className="detail">
                        <div className="label">Campaign Status</div>
                        <div className="value">{campaign.status}</div>
                      </div>
                    </CampaignDetails>

                    {campaign.description && (
                      <p><strong>Description:</strong> {campaign.description}</p>
                    )}

                    <p>
                      <strong>Duration:</strong> {new Date(campaign.startDate).toLocaleDateString()} 
                      {' - '} {new Date(campaign.endDate).toLocaleDateString()}
                    </p>

                    <UpvoteButton 
                      upvoted={campaign.isUpvoted}
                      onClick={() => handleCampaignUpvote(campaign.campaignId, campaign.isUpvoted)}
                      disabled={campaignTogglingId === campaign.campaignId}
                    >
                      {campaign.isUpvoted ? '✓ Interested' : '☆ Show Interest'}
                    </UpvoteButton>
                  </CampaignCard>
                ))}
              </CampaignsGrid>
            )}
          </ListingsContainer>
        )}

        {activeTab === 'create' && (
          <div>
            <SellerListingForm onListingCreated={refresh} />
          </div>
        )}
      </TabContainer>

      {showModal && selectedListing && (
        <ListingDetailsModal listing={selectedListing} onClose={handleCloseModal} />
      )}
    </DashboardContainer>
  );
};

export default SellerDashboard;
